import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePlants } from '../hooks/usePlants';
import { useToast } from '../context/ToastContext';
import { isValidNickname, isValidLocation, isNotFutureDate, ValidationErrorMessages } from '../utils/validation';
import { formatDate } from '../utils/formatters';

export default function EditPlant() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { getPlantById, updatePlant, deletePlant, loading: plantsLoading } = usePlants();

  const plant = id ? getPlantById(id) : undefined;

  const [nickname, setNickname] = useState('');
  const [location, setLocation] = useState('');
  const [acquiredDate, setAcquiredDate] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (plant) {
      setNickname(plant.nickname || '');
      setLocation(plant.location || '');
      if (plant.acquiredDate) {
        const date = plant.acquiredDate.toDate();
        const dateStr = date.toISOString().split('T')[0] ?? '';
        setAcquiredDate(dateStr);
      }
    }
  }, [plant]);

  const handleFieldChange = (setter: (value: string) => void) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setter(e.target.value);
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!id || !plant) return;
    const newErrors: Record<string, string> = {};
    if (nickname && !isValidNickname(nickname)) newErrors.nickname = ValidationErrorMessages.NICKNAME_TOO_LONG;
    if (location && !isValidLocation(location)) newErrors.location = ValidationErrorMessages.LOCATION_TOO_LONG;
    const parsedDate = acquiredDate ? new Date(acquiredDate) : null;
    if (parsedDate && !isNotFutureDate(parsedDate)) newErrors.acquiredDate = ValidationErrorMessages.ACQUIRED_DATE_FUTURE;
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    setIsSaving(true);
    const result = await updatePlant(id, {
      nickname: nickname.trim() || null,
      location: location.trim() || null,
      acquiredDate: parsedDate,
    });
    setIsSaving(false);
    if (result.success) {
      showSuccess('Plant updated!');
      setIsDirty(false);
      navigate(`/plant/${id}`);
    } else {
      showError(result.error || 'Failed to update plant');
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setIsDeleting(true);
    const result = await deletePlant(id);
    setIsDeleting(false);
    if (result.success) {
      showSuccess('Plant deleted');
      navigate('/home');
    } else {
      showError(result.error || 'Failed to delete plant');
    }
  };

  if (plantsLoading) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center">
        <div role="status" aria-live="polite" className="flex flex-col items-center gap-3">
          <span className="material-symbols-outlined text-primary animate-pulse" style={{ fontSize: 32 }} aria-hidden="true">eco</span>
          <span className="text-gray-500 font-serif text-sm">Loading...</span>
        </div>
      </main>
    );
  }

  if (!plant) {
    return (
      <main className="min-h-screen bg-background p-6">
        <div className="text-center py-12">
          <p className="text-gray-500 font-serif">Plant not found</p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background">
      <div className="mx-auto max-w-md p-6 flex flex-col gap-8">
        {/* Plant preview card */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <span className="h-px flex-1 bg-gray-200" />
            <span className="section-header">Plant Preview</span>
            <span className="h-px flex-1 bg-gray-200" />
          </div>
          <div className="flex gap-5 items-center p-4 border border-gray-200 rounded-lg bg-white">
            <div className="shrink-0 w-20 h-20 rounded-full overflow-hidden border border-gray-100">
              <img src={plant.photoURL} alt={plant.commonName} className="h-full w-full object-cover" />
            </div>
            <div>
              <h2 className="text-2xl font-display font-bold text-editorial-black leading-tight">
                {plant.nickname || plant.commonName}
              </h2>
              {plant.scientificName && (
                <p className="text-gray-500 italic font-serif text-lg">{plant.scientificName}</p>
              )}
            </div>
          </div>
        </section>

        {/* Form */}
        <form
          onSubmit={(e) => { e.preventDefault(); handleSave(); }}
          className="flex flex-col gap-8"
        >
          {/* Display Name */}
          <div className="group">
            <label htmlFor="nickname" className="section-header mb-1 block group-focus-within:text-editorial-black transition-colors">
              Display Name
            </label>
            <input
              id="nickname"
              type="text"
              value={nickname}
              onChange={handleFieldChange(setNickname)}
              placeholder={plant.commonName}
              maxLength={50}
              className="input-editorial"
            />
            {plant.scientificName && (
              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => { setNickname(plant.scientificName || ''); setIsDirty(true); }}
                  className="inline-flex items-center px-3 py-1.5 rounded-full border border-gray-200 bg-white text-xs font-sans text-gray-600 hover:border-gray-400 transition-colors"
                >
                  Use &quot;{plant.scientificName}&quot;
                </button>
                {nickname && (
                  <button
                    type="button"
                    onClick={() => { setNickname(''); setIsDirty(true); }}
                    className="inline-flex items-center px-3 py-1.5 rounded-full border border-gray-200 bg-white text-xs font-sans text-gray-600 hover:border-gray-400 transition-colors"
                  >
                    Reset to default
                  </button>
                )}
              </div>
            )}
            {errors.nickname && <p className="mt-1 text-sm font-sans text-danger">{errors.nickname}</p>}
          </div>

          {/* Location */}
          <div className="group">
            <label htmlFor="location" className="section-header mb-1 block group-focus-within:text-editorial-black transition-colors">
              Location
            </label>
            <div className="relative">
              <input
                id="location"
                type="text"
                value={location}
                onChange={handleFieldChange(setLocation)}
                placeholder="e.g., Living Room"
                maxLength={100}
                className="input-editorial pr-8"
              />
              <span className="material-symbols-outlined absolute right-0 top-3 text-gray-400 text-xl pointer-events-none" aria-hidden="true">
                pin_drop
              </span>
            </div>
            {errors.location && <p className="mt-1 text-sm font-sans text-danger">{errors.location}</p>}
          </div>

          {/* Acquired Date */}
          <div className="group">
            <label htmlFor="acquiredDate" className="section-header mb-1 block group-focus-within:text-editorial-black transition-colors">
              Acquired Date
            </label>
            <input
              id="acquiredDate"
              type="date"
              value={acquiredDate}
              onChange={handleFieldChange(setAcquiredDate)}
              max={new Date().toISOString().split('T')[0]}
              className="input-editorial"
            />
            {errors.acquiredDate && <p className="mt-1 text-sm font-sans text-danger">{errors.acquiredDate}</p>}
          </div>

          {/* AI-Generated Metadata */}
          <section className="p-5 border border-gray-200 bg-gray-50 rounded-lg relative overflow-hidden">
            <div className="absolute top-0 right-0 p-2 opacity-10">
              <span className="material-symbols-outlined text-4xl" aria-hidden="true">smart_toy</span>
            </div>
            <h3 className="section-header mb-4 flex items-center gap-2">
              <span className="material-symbols-outlined text-sm" aria-hidden="true">auto_awesome</span>
              AI-Generated Metadata
            </h3>
            <dl className="grid grid-cols-1 gap-y-4">
              <div className="flex justify-between items-baseline border-b border-gray-100 pb-2">
                <dt className="text-xs font-sans text-gray-400 uppercase tracking-wider">Scientific Name</dt>
                <dd className="text-base font-display italic text-editorial-black">{plant.scientificName || 'N/A'}</dd>
              </div>
              <div className="flex justify-between items-baseline">
                <dt className="text-xs font-sans text-gray-400 uppercase tracking-wider">Added</dt>
                <dd className="text-base font-display text-editorial-black">{formatDate(plant.createdAt)}</dd>
              </div>
            </dl>
          </section>

          {/* Save button */}
          <button
            type="submit"
            disabled={isSaving || !isDirty}
            className="btn-editorial group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
            <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform" style={{ fontSize: 20 }} aria-hidden="true">
              arrow_forward
            </span>
          </button>
        </form>

        {/* Delete section */}
        <section className="border border-red-200 rounded-lg p-5 bg-red-50">
          <div className="flex items-start gap-3 mb-4">
            <span className="material-symbols-outlined text-red-500 mt-0.5" aria-hidden="true">warning</span>
            <div>
              <h3 className="text-red-600 font-display font-bold text-lg leading-tight">Delete Plant</h3>
              <p className="text-red-500/80 text-sm font-sans mt-1">
                This action cannot be undone. All tracking history will be lost.
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isDeleting}
            className="w-full border border-red-500/30 text-red-600 h-12 rounded flex items-center justify-center gap-2 hover:bg-red-100 transition-colors uppercase tracking-widest text-xs font-bold font-sans disabled:opacity-50"
          >
            <span className="material-symbols-outlined text-lg" aria-hidden="true">delete</span>
            Delete Plant
          </button>
        </section>

        {/* Delete confirmation dialog */}
        {showDeleteConfirm && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-dialog-title"
          >
            <div className="w-full max-w-sm rounded-lg bg-white border border-gray-200 p-6 shadow-elevated">
              <h3 id="delete-dialog-title" className="text-lg font-display font-bold text-editorial-black">
                Delete Plant?
              </h3>
              <p className="mt-2 font-serif text-gray-600">
                Are you sure you want to delete &quot;{plant.nickname || plant.commonName}&quot;?
                This action cannot be undone.
              </p>
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="flex flex-1 h-btn-md items-center justify-center gap-2 rounded-md bg-danger px-4 font-sans font-bold text-sm uppercase tracking-wider text-white hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  {isDeleting ? (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
